import { apiRequest, apiSecureRequest, investmentEntityRequest, orderGroupInvestmentRequest } from '@utils/request';
import {
	CheckoutAutoInvestDetails,
	CheckoutOrderGroupDetails,
	CheckoutPaymentAmountDetails,
	CheckoutReviewOrderDetails,
	CheckoutSuccess,
	CheckoutType,
	EntityAccountDetails,
	IndividualAccountContactDetails,
	IndividualAccountDetails,
	JointAccountDetails,
	SignupCheckoutSelectionResponse,
	TrustAccountDetails
} from 'types/checkout';
import { Country, TaxInfo } from 'types/form';
import { DripStatus, InvestmentEntity, InvestmentEntityType } from 'types/investment-entity';

import { DocumentAcknowledgmentsResponse, DocumentListItem } from 'types/documents';
import axios from 'axios';
import { getDistinctId } from '@utils/analytics';
import { hdyhOtherField } from '@constants';
import { orderForm } from '@store/modules/order-form';
import { OrderGroupResponse } from 'types/add-funds';
import { RiaPlan } from 'types/plan';
import { ServiceLevelName } from 'types/service-level';

export async function submitHdyhSurvey(
	primaryChoice: string,
	secondaryChoice: string,
	otherChoice: string
): Promise<boolean> {
	const motiveAnswer = '';
	let hdyhAnswer = '';
	let hdyhAnswerFollowUp = '';

	if (primaryChoice === hdyhOtherField) {
		hdyhAnswer = otherChoice;
	} else {
		hdyhAnswer = primaryChoice;
	}

	if (secondaryChoice === hdyhOtherField) {
		hdyhAnswerFollowUp = otherChoice;
	} else {
		hdyhAnswerFollowUp = secondaryChoice;
	}

	const surveyParams = hdyhAnswerFollowUp ? { hdyhAnswer, hdyhAnswerFollowUp } : { hdyhAnswer, motiveAnswer };

	const response = await apiSecureRequest.post('/hdyh-survey', surveyParams);
	return response.status === 200;
}

export async function getSignupCheckoutSelections(): Promise<SignupCheckoutSelectionResponse> {
	const response = await apiSecureRequest.get('/signup-checkout');
	return response.data;
}

export async function updateQuickInvest(quickInvest: boolean): Promise<SignupCheckoutSelectionResponse> {
	const response = await apiSecureRequest.post('/signup-checkout/quick-invest', { quickInvest });
	return response.data;
}

export async function updateInvestmentSelections(plan: RiaPlan, accountLevel: ServiceLevelName | null) {
	const response = await apiSecureRequest.post('/signup-checkout/investment-selections', {
		accountLevel,
		plan: plan.investmentStrategy,
		planType: plan.planType
	});

	return response.data;
}

export async function getCountries(): Promise<Array<Country>> {
	const response = await apiRequest.get('/countries');
	return response.data;
}

export async function isEligibleForAdditionalInvestment(id: string): Promise<boolean> {
	const response = await apiSecureRequest.get(`/investment-entity/${id}/add-funds-eligibility`);
	return response.data.additionalInvestmentEligibility === 'VALID';
}

export async function isEligibleForRecurringInvestment(): Promise<boolean> {
	const response = await investmentEntityRequest.get('/recurring-investment-eligibility');
	return response.data.recurringInvestmentEligibilityModeEnum !== 'NOT_ELIGIBLE';
}

export async function submitIndividualAccountDetails(payload: IndividualAccountDetails): Promise<void> {
	await investmentEntityRequest.post('/individual-account', payload);
}

export async function submitNewEntityAccountDetails(payload: EntityAccountDetails): Promise<InvestmentEntity> {
	const response = await apiSecureRequest.post('/investment-entity/entity-account', payload);
	return response.data;
}

export async function updateExistingEntityAccountDetails(payload: EntityAccountDetails): Promise<void> {
	await investmentEntityRequest.put(`/entity-account`, payload);
}

export async function getEntityAccountDetails(investmentEntityId: string): Promise<EntityAccountDetails> {
	const response = await apiSecureRequest.get(`/investment-entity/${investmentEntityId}/entity-account`);
	return response.data;
}

export async function submitNewJointAccountDetails(payload: JointAccountDetails): Promise<InvestmentEntity> {
	const response = await apiSecureRequest.post('/investment-entity/joint-account', payload);
	return response.data;
}

export async function updateExistingJointAccountDetails(payload: JointAccountDetails): Promise<void> {
	await investmentEntityRequest.put(`/joint-account`, payload);
}

export async function getJointAccountDetails(): Promise<JointAccountDetails> {
	const response = await investmentEntityRequest.get(`/joint-account`);
	return response.data;
}

export async function submitNewTrustAccountDetails(payload: TrustAccountDetails): Promise<InvestmentEntity> {
	const response = await apiSecureRequest.post(`/investment-entity/trust-account`, payload);
	return response.data;
}

export async function updateExistingTrustAccountDetails(payload: TrustAccountDetails): Promise<void> {
	await investmentEntityRequest.put(`/trust-account`, payload);
}

export async function getTrustAccountDetails(): Promise<TrustAccountDetails> {
	const response = await investmentEntityRequest.get(`/trust-account`);
	return response.data;
}

export async function submitIndividualAccountTaxReportingDetails(payload: TaxInfo): Promise<void> {
	await investmentEntityRequest.post('/individual-account/tax-details', payload);
}

export async function submitIndividualAccountContactDetails(payload: IndividualAccountContactDetails): Promise<void> {
	await investmentEntityRequest.post('/individual-account/contact-details', payload);
}

export async function getCheckoutPaymentAmountDetails(
	serviceLevel: ServiceLevelName,
	planId: string
): Promise<CheckoutPaymentAmountDetails> {
	const responses = await axios.all([
		investmentEntityRequest.get(`/service-level/${serviceLevel}/ria-plan/${planId}/investment-range`),
		investmentEntityRequest.get('/recurring-investment-eligibility')
	]);

	return {
		investmentRange: responses[0].data,
		autoInvestEligibility: responses[1].data.recurringInvestmentEligibilityModeEnum !== 'NOT_ELIGIBLE'
	};
}

export async function createOrderGroup(payload: CheckoutOrderGroupDetails): Promise<OrderGroupResponse> {
	const response = await investmentEntityRequest.post('/investment/order-group', payload);
	return response.data;
}

export async function updateAutoInvest(payload: CheckoutAutoInvestDetails): Promise<void> {
	await orderGroupInvestmentRequest.post('/auto-invest', payload);
}

export async function updateSignUpCheckoutType(checkoutType: CheckoutType): Promise<void> {
	const mixpanelId = getDistinctId();
	await apiRequest.post(`/lead/${mixpanelId}/sign-up-checkout-type`, { signUpCheckoutType: checkoutType });
}

export async function updateDrip(dripPreference: DripStatus): Promise<void> {
	await orderGroupInvestmentRequest.post('/drip', { reinvestmentPreference: dripPreference });
}

export async function completeGiftedCheckout(): Promise<void> {
	await investmentEntityRequest.post('/gifted-account/complete-checkout');
}

function filterToSubscriptionType(data: DocumentAcknowledgmentsResponse): DocumentListItem[] {
	return data?.reitDocuments.filter((document: DocumentListItem) => {
		return document.documentType === 'SUBSCRIPTION_AGREEMENT';
	});
}

export async function getCheckoutReviewOrderDetails(
	entityType: InvestmentEntityType
): Promise<CheckoutReviewOrderDetails> {
	let detailsEndpoint = '';

	if (entityType === 'JOINT_ACCOUNT') {
		detailsEndpoint = '/joint-account';
	} else if (entityType === 'LLC') {
		detailsEndpoint = '/entity-account';
	} else if (entityType === 'TRUST') {
		detailsEndpoint = '/trust-account';
	} else {
		detailsEndpoint = '/current-address';
	}

	const responses = await axios.all([
		investmentEntityRequest.get(detailsEndpoint),
		orderForm.isUnclaimedGiftedAccount
			? investmentEntityRequest.get(`/gifted-account/acknowledgments`)
			: orderGroupInvestmentRequest.get('/signed-agreements'),
		apiSecureRequest.get('/user/fee-waiver/details')
	]);

	return {
		details: responses[0].data,
		signedAgreements: orderForm.isUnclaimedGiftedAccount
			? filterToSubscriptionType(responses[1].data)
			: responses[1].data,
		feeWaiverDetails: responses[2].data
	};
}

export const placeOrder = async (
	orderGroupId: string,
	documentIds: Array<string>,
	reinvestmentPreferenceEnum: DripStatus | null,
	autoInvestDetails: CheckoutAutoInvestDetails | null = null
): Promise<OrderGroupResponse> => {
	const response = await investmentEntityRequest.post(`/investment/order-group/${orderGroupId}/place-order`, {
		reitDocumentRevisionIds: documentIds,
		reinvestmentPreference: reinvestmentPreferenceEnum,
		addRecurringInvestmentSchedule: autoInvestDetails?.addRecurringInvestmentSchedule || false,
		recurringAmount: autoInvestDetails?.recurringAmount,
		frequency: autoInvestDetails?.frequency,
		firstInvestmentDayOfMonth: autoInvestDetails?.firstInvestmentDayOfMonth,
		dayOfWeek: autoInvestDetails?.investmentDayOfWeek,
		startDate: autoInvestDetails?.startDate
	});
	return response.data;
};

export async function getCheckoutSuccess(): Promise<CheckoutSuccess> {
	const response = await investmentEntityRequest.get('/checkout-success');
	return response.data as CheckoutSuccess;
}
